import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { 
  parseISO, 
  format, 
  startOfWeek, 
  addDays, 
} from 'date-fns';
import { ru, uz, uzCyrl, enGB } from 'date-fns/locale';
import { timeSlotsBranchServices } from 'api/services';
import { IResDateForReservation } from 'api/services/timeslots-branches/types';
import { useSelector } from 'react-redux';
import { AppState } from 'store/store';
import './ScheduleQueue.sass';

const locales: Record<string, any> = { ru, uz, oz: uzCyrl, en: enGB };

const ScheduleQueue: React.FC = () => {
  const [data, setData] = useState<IResDateForReservation[]>([]);
  const { lang } = useSelector((state: AppState) => state.i18n) as any;
  const { user } = useSelector((state: AppState) => state.users);
  const navigate = useNavigate();
  const currentLocale = locales[lang] || ru;
  const i18n = useSelector(
		(state: AppState) => state.i18n.data.components.ScheduleQueue,
	);

  useEffect(() => {
    if(!user?.branchId) return
    getData();

    const interval = setInterval(() => {
      getData();
    }, 5000);

    return () => clearInterval(interval);
  }, [user?.branchId]);

  const getData = async () => {
    const params = {
      branchId: user?.branchId
    }
    const res = await timeSlotsBranchServices.getDatesForReservation(params).then((res) => res.data)
    setData(res)
  }

  const weekDaysHeader = useMemo(() => {
    const start = startOfWeek(new Date(), { locale: currentLocale, weekStartsOn: 1 });
    return [...Array(7)].map((_, i) => 
      format(addDays(start, i), 'EEEE', { locale: currentLocale })
    );
  }, [currentLocale]);

  const calendarGrid = useMemo(() => {
    if (!data || data.length === 0) return [];

    const firstDate = parseISO(data[0].date);
    const startOfTargetWeek = startOfWeek(firstDate, { weekStartsOn: 1 });
    const gridStart = addDays(startOfTargetWeek, -7);
    const allDays = [...Array(28)].map((_, i) => addDays(gridStart, i));

    return allDays.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayData = data.find(item => format(parseISO(item.date), 'yyyy-MM-dd') === dateStr);

      return {
        date: day,
        dayData: dayData,
        isAvailable: dayData ? (dayData.capacity > dayData.reserved) : false
      };
    });
  }, [data]);

  const monthTitle = useMemo(() => {
    if (calendarGrid.length === 0) return '';

    const months = calendarGrid.reduce((acc: string[], curr) => {
      const monthName = format(curr.date, 'LLLL', { locale: currentLocale });
      if (!acc.includes(monthName)) {
        acc.push(monthName);
      }
      return acc;
    }, []);

    return months
      .map(m => m.charAt(0).toUpperCase() + m.slice(1))
      .join(' - ');
  }, [calendarGrid, currentLocale]);

  return (
    <div className='schedule-queue'>
      <div className="schedule-queue__title">
        {monthTitle}
      </div>

      <div className="schedule-queue__header">
        {weekDaysHeader.map(d => (
          <div key={d} className="schedule-queue__header-cell">{d}</div>
        ))}
      </div>

      <div className="schedule-queue__body">
        {calendarGrid.map(({ date, dayData, isAvailable }, idx) => {
          let cellClass = "schedule-queue__cell";
          if (!dayData) cellClass += " schedule-queue__cell--no-data";
          if (isAvailable) cellClass += " schedule-queue__cell--active";
          if (dayData && !isAvailable) cellClass += " schedule-queue__cell--full";

          return (
            <button
              key={idx}
              type="button"
              disabled={!isAvailable}
              className={cellClass}
              onClick={() => {
                if (isAvailable) {
                  navigate(`/schedule/${format(date, 'yyyy-MM-dd')}?branchId=${user?.branchId}`);
                }
              }}
            >
              <div className="schedule-queue__cell-content">
                <div className="schedule-queue__day-num">
                  {format(date, 'dd.MM.yyyy')}
                </div>
              
                {dayData && (
                  <div className="schedule-queue__slots-info">
                    {lang === 'uz' || lang === 'oz' ? (
                      <>{dayData.capacity} {i18n.of} {dayData.capacity - dayData.reserved} <br/> {i18n.available}</>
                    ) : (
                      <>{i18n.available} <br/> {dayData.capacity - dayData.reserved} {i18n.of} {dayData.capacity}</>
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleQueue;